import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Menu as MenuIcon, Phone, MapPin, UtensilsCrossed, CalendarCheck, Instagram, Facebook } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useBooking } from "@/context/BookingContext";
import { track } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Il Menù" },
  { to: "/storia", label: "La Nostra Storia" },
  { to: "/galleria", label: "Galleria" },
  { to: "/eventi", label: "Eventi" },
  { to: "/aziende", label: "Gruppi ed eventi" },
  { to: "/contatti", label: "Contatti" },
];

export default function Layout({ children }) {
  const { settings } = useSettings();
  const { open: openBooking } = useBooking();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const tel = (settings.phone_primary || "").replace(/\s/g, "");

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-cream/90 border-b border-brass/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex flex-col leading-none" data-testid="brand-logo">
              <span className="font-serif text-xl sm:text-2xl font-bold text-bordeaux-dark tracking-tight">Ristorante Masetti</span>
              <span className="font-accent italic text-xs sm:text-sm text-oak/70 tracking-widest">Cucina bolognese · dal 1961</span>
            </Link>
            <nav className="hidden lg:flex items-center gap-7">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                  className={`text-sm font-medium transition-colors hover:text-bordeaux ${location.pathname === n.to ? "text-bordeaux" : "text-oak/90"}`}>
                  {n.label}
                </Link>
              ))}
              {tel && (
                <a href={`tel:${tel}`} onClick={() => track("call_click", "header")} className="text-sm font-medium text-oak/90 hover:text-bordeaux flex items-center gap-1.5" data-testid="header-call">
                  <Phone className="w-4 h-4" />Chiama
                </a>
              )}
              <Button onClick={() => openBooking("header")}
                className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-5" data-testid="header-prenota-btn">
                Prenota un Tavolo
              </Button>
            </nav>
            <div className="lg:hidden flex items-center gap-2">
              <Button onClick={() => openBooking("header-mobile")} size="sm"
                className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full" data-testid="header-prenota-mobile">
                Prenota
              </Button>
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><MenuIcon className="w-6 h-6 text-bordeaux" /></Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-cream border-brass/20 w-72">
                  <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
                  <div className="flex flex-col gap-1 mt-8">
                    {NAV.map((n) => (
                      <Link key={n.to} to={n.to} onClick={() => setSheetOpen(false)} data-testid={`mobilenav-${n.to === "/" ? "home" : n.to.slice(1)}`}
                        className="font-serif text-lg text-oak py-2 border-b border-brass/10 hover:text-bordeaux">
                        {n.label}
                      </Link>
                    ))}
                    <Link to="/resta-in-contatto" onClick={() => setSheetOpen(false)} className="font-serif text-lg text-oak py-2 border-b border-brass/10 hover:text-bordeaux">Resta in contatto</Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-20 lg:pb-0">{children}</main>

      <footer className="bg-oak-dark text-cream/90 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <h3 className="font-serif text-2xl text-brass mb-2">Ristorante Masetti</h3>
            <p className="font-accent italic text-lg text-cream/70">{settings.tagline || "La cucina bolognese, per stare insieme."}</p>
            <div className="flex gap-3 mt-4">
              {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noreferrer" onClick={() => track("instagram_click")} data-testid="footer-instagram" className="hover:text-brass"><Instagram className="w-5 h-5" /></a>}
              {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noreferrer" onClick={() => track("facebook_click")} data-testid="footer-facebook" className="hover:text-brass"><Facebook className="w-5 h-5" /></a>}
            </div>
          </div>
          <div className="text-sm space-y-1">
            <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-brass" /> {settings.address}, {settings.city}</p>
            {settings.phone_primary && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-brass" /> {settings.phone_primary}{settings.phone_primary_name ? ` (${settings.phone_primary_name})` : ""}</p>}
            {settings.phone_secondary && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-brass" /> {settings.phone_secondary}{settings.phone_secondary_name ? ` (${settings.phone_secondary_name})` : ""}</p>}
            {settings.email && <p>{settings.email}</p>}
          </div>
          <div className="text-sm space-y-1">
            {(settings.hours || []).map((h) => (
              <p key={h.day} className="flex justify-between border-b border-cream/10 py-0.5"><span className="text-cream/70">{h.day}</span><span>{h.value}</span></p>
            ))}
          </div>
        </div>
        <div className="border-t border-cream/10 py-4 text-center text-xs text-cream/50">
          © {new Date().getFullYear()} Ristorante Masetti · Via Gesso 70, Zola Predosa (BO) · <Link to="/privacy" className="hover:text-brass" data-testid="footer-privacy-link">Privacy</Link> · <Link to="/admin" className="hover:text-brass" data-testid="footer-admin-link">Area riservata</Link>
        </div>
      </footer>

      {/* Mobile floating bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-cream/95 backdrop-blur border-t border-brass/20 grid grid-cols-3 shadow-[0_-4px_20px_rgba(42,24,13,0.08)]">
        <Link to="/menu" className="flex flex-col items-center py-2.5 text-oak active:bg-brass/10" data-testid="floating-menu">
          <UtensilsCrossed className="w-5 h-5 text-bordeaux" /><span className="text-[11px] mt-0.5">Menù</span>
        </Link>
        <button onClick={() => openBooking("floating")} className="flex flex-col items-center py-2.5 text-oak active:bg-brass/10" data-testid="floating-prenota">
          <CalendarCheck className="w-5 h-5 text-bordeaux" /><span className="text-[11px] mt-0.5">Prenota</span>
        </button>
        {tel ? (
          <a href={`tel:${tel}`} onClick={() => track("call_click", "floating")} className="flex flex-col items-center py-2.5 text-oak active:bg-brass/10" data-testid="floating-call">
            <Phone className="w-5 h-5 text-bordeaux" /><span className="text-[11px] mt-0.5">Chiama</span>
          </a>
        ) : (
          <span className="flex flex-col items-center py-2.5 text-oak/40"><Phone className="w-5 h-5" /><span className="text-[11px] mt-0.5">Chiama</span></span>
        )}
      </div>
    </div>
  );
}
